import {
  pgTable,
  text,
  serial,
  integer,
  boolean,
  timestamp,
  decimal,
  varchar,
  jsonb,
  index,
  date,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

export const marketingCampaigns = pgTable("marketing_campaigns", {
  id: serial("id").primaryKey(),
  name: text("name").notNull(),
  type: text("type").notNull(), // email, social, webinar, direct_mail, referral
  status: text("status").notNull().default("active"), // active, paused, completed, draft
  budget: decimal("budget", { precision: 10, scale: 2 }),
  spent: decimal("spent", { precision: 10, scale: 2 }).default("0"),
  leads: integer("leads").default(0),
  conversions: integer("conversions").default(0),
  startDate: timestamp("start_date").notNull(),
  endDate: timestamp("end_date"),
  description: text("description"),
  agencyId: varchar("agency_id"),
  createdAt: timestamp("created_at").defaultNow(),
}, (table) => ({
  agencyIdIdx: index("idx_marketing_campaigns_agency_id").on(table.agencyId),
}));

// Insert schemas
export const insertMarketingCampaignSchema = createInsertSchema(
  marketingCampaigns
).omit({ id: true, createdAt: true });

// Types
export type MarketingCampaign = typeof marketingCampaigns.$inferSelect;
export type InsertMarketingCampaign = z.infer<
  typeof insertMarketingCampaignSchema
>;

// Email Template Folders
export const emailTemplateFolders = pgTable(
  "email_template_folders",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    description: text("description"),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("idx_email_template_folders_agency_id").on(
      table.agencyId
    ),
  })
);

// Email Templates
export const emailTemplates = pgTable(
  "email_templates",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    subject: text("subject").notNull(),
    htmlContent: text("html_content"),
    textContent: text("text_content"),
    componentStructure: jsonb("component_structure"),
    folderId: integer("folder_id"),
    isActive: boolean("is_active").default(true),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    folderIdIdx: index("idx_email_templates_folder_id").on(table.folderId),
    agencyIdIdx: index("idx_email_templates_agency_id").on(table.agencyId),
    isActiveIdx: index("idx_email_templates_is_active").on(table.isActive),
  })
);

// Component Structure Schema for Visual Editor
export const emailComponentSchema = z.object({
  id: z.string(),
  type: z.enum(["text", "heading", "image", "button", "spacer", "divider"]),
  content: z.string(),
  styles: z.record(z.string()).optional(),
  properties: z.record(z.any()).optional(),
});

export const componentStructureSchema = z.array(emailComponentSchema).optional();

// Insert Schemas
export const insertEmailTemplateFolderSchema = createInsertSchema(
  emailTemplateFolders
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertEmailTemplateSchema = createInsertSchema(emailTemplates)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    componentStructure: componentStructureSchema.optional(),
  });

// Types
export type EmailTemplateFolder = typeof emailTemplateFolders.$inferSelect;
export type InsertEmailTemplateFolder = z.infer<
  typeof insertEmailTemplateFolderSchema
>;

export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;

export type EmailComponent = z.infer<typeof emailComponentSchema>;

// Email DNC List
export const emailDncList = pgTable(
  "email_dnc_list",
  {
    id: serial("id").primaryKey(),
    email: varchar("email", { length: 255 }).notNull(),
    reason: varchar("reason", { length: 50 }).notNull(),
    agencyId: varchar("agency_id"),
    addedBy: varchar("added_by"),
    addedAt: timestamp("added_at").defaultNow(),
  },
  (table) => ({
    emailAgencyUnique: uniqueIndex("email_dnc_list_email_agency_unique").on(
      table.email,
      table.agencyId
    ),
    emailIdx: index("email_dnc_list_email_idx").on(table.email),
    agencyIdIdx: index("email_dnc_list_agency_id_idx").on(table.agencyId),
  })
);

// DNC Reason Schema
export const dncReasonSchema = z.enum([
  "unsubscribed",
  "bounced",
  "complaint",
  "manual",
]);

// Insert Schema
export const insertEmailDncSchema = createInsertSchema(emailDncList)
  .omit({ id: true, addedAt: true })
  .extend({
    email: z.string().email("Invalid email address"),
    reason: dncReasonSchema,
  });

// Full Schema
export const emailDncSchema = insertEmailDncSchema.extend({
  id: z.number(),
  addedAt: z.date(),
});

// Types
export type EmailDncEntry = typeof emailDncList.$inferSelect;
export type InsertEmailDncEntry = z.infer<typeof insertEmailDncSchema>;

// Email Lists (Smart Lists - stores specific contacts)
export const emailLists = pgTable(
  "email_lists",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    description: text("description"),
    contactIds: jsonb("contact_ids").notNull().default([]), // Array of contact IDs: ["client-1", "prospect-2", ...]
    contactCount: integer("contact_count").notNull(),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("idx_email_lists_agency_id").on(table.agencyId),
  })
);

// Email List Contact IDs Schema (array of strings like "client-1", "prospect-2")
export const emailListContactIdsSchema = z.array(z.string()).min(0);

// Insert Schema
export const insertEmailListSchema = createInsertSchema(emailLists)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    contactIds: emailListContactIdsSchema, // Required for new lists
    contactCount: z.number().int().min(0),
    agencyId: z.string().optional(),
  });

// Email Send Events (for analytics tracking)
export const emailSendEvents = pgTable(
  "email_send_events",
  {
    id: serial("id").primaryKey(),
    emailId: integer("email_id").notNull(), // references emails.id
    eventType: varchar("event_type", { length: 50 }).notNull(), // 'sent', 'delivered', 'opened', 'clicked', 'bounced', 'failed'
    eventData: jsonb("event_data"), // additional event metadata
    occurredAt: timestamp("occurred_at").defaultNow(),
  },
  (table) => ({
    emailIdIdx: index("email_send_events_email_id_idx").on(table.emailId),
    eventTypeIdx: index("email_send_events_event_type_idx").on(table.eventType),
    emailIdEventTypeIdx: index("email_send_events_email_id_event_type_idx").on(
      table.emailId,
      table.eventType
    ),
  })
);

export const insertEmailSendEventSchema = createInsertSchema(emailSendEvents)
  .omit({ id: true, occurredAt: true })
  .extend({
    eventType: z.enum(["sent", "delivered", "opened", "clicked", "bounced", "failed"]),
    eventData: z.record(z.any()).optional(),
  });

export type EmailSendEvent = typeof emailSendEvents.$inferSelect;
export type InsertEmailSendEvent = z.infer<typeof insertEmailSendEventSchema>;

// Types
export type EmailList = typeof emailLists.$inferSelect;
export type InsertEmailList = z.infer<typeof insertEmailListSchema>;

// SMS Template Folders
export const smsTemplateFolders = pgTable(
  "sms_template_folders",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    description: text("description"),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("idx_sms_template_folders_agency_id").on(
      table.agencyId
    ),
  })
);

// SMS Templates
export const smsTemplates = pgTable(
  "sms_templates",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    content: text("content").notNull(), // SMS message content (text only, max 1600 chars)
    folderId: integer("folder_id"),
    isActive: boolean("is_active").default(true),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    folderIdIdx: index("idx_sms_templates_folder_id").on(table.folderId),
    agencyIdIdx: index("idx_sms_templates_agency_id").on(table.agencyId),
  })
);

// Insert Schemas
export const insertSmsTemplateFolderSchema = createInsertSchema(
  smsTemplateFolders
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertSmsTemplateSchema = createInsertSchema(smsTemplates)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    content: z.string().max(1600, "SMS content cannot exceed 1600 characters"),
  });

// Types
export type SmsTemplateFolder = typeof smsTemplateFolders.$inferSelect;
export type InsertSmsTemplateFolder = z.infer<
  typeof insertSmsTemplateFolderSchema
>;

export type SmsTemplate = typeof smsTemplates.$inferSelect;
export type InsertSmsTemplate = z.infer<typeof insertSmsTemplateSchema>;

// SMS DNC List
export const smsDncList = pgTable(
  "sms_dnc_list",
  {
    id: serial("id").primaryKey(),
    phone: varchar("phone", { length: 20 }).notNull(), // E.164 format: +1234567890
    reason: varchar("reason", { length: 50 }).notNull(),
    agencyId: varchar("agency_id"),
    addedBy: varchar("added_by"),
    addedAt: timestamp("added_at").defaultNow(),
  },
  (table) => ({
    phoneAgencyUnique: uniqueIndex("sms_dnc_list_phone_agency_unique").on(
      table.phone,
      table.agencyId
    ),
    phoneIdx: index("sms_dnc_list_phone_idx").on(table.phone),
    agencyIdIdx: index("sms_dnc_list_agency_id_idx").on(table.agencyId),
  })
);

// SMS DNC Reason Schema (reuse dncReasonSchema but add SMS-specific reasons)
export const smsDncReasonSchema = z.enum([
  "unsubscribed",
  "bounced",
  "complaint",
  "manual",
  "stop_reply",
  "invalid",
]);

// Insert Schema
export const insertSmsDncSchema = createInsertSchema(smsDncList)
  .omit({ id: true, addedAt: true })
  .extend({
    phone: z.string().regex(/^\+[1-9]\d{1,14}$/, "Phone must be in E.164 format (+1234567890)"),
    reason: smsDncReasonSchema,
  });

// Types
export type SmsDncEntry = typeof smsDncList.$inferSelect;
export type InsertSmsDncEntry = z.infer<typeof insertSmsDncSchema>;

// SMS Lists (Smart Lists - stores specific contacts)
export const smsLists = pgTable(
  "sms_lists",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    description: text("description"),
    contactIds: jsonb("contact_ids").notNull().default([]), // Array of contact IDs: ["client-1", "prospect-2", ...]
    contactCount: integer("contact_count").notNull(),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("idx_sms_lists_agency_id").on(table.agencyId),
  })
);

// SMS List Contact IDs Schema (reuse emailListContactIdsSchema)
export const smsListContactIdsSchema = emailListContactIdsSchema;

// Insert Schema
export const insertSmsListSchema = createInsertSchema(smsLists)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    contactIds: smsListContactIdsSchema, // Required for new lists
    contactCount: z.number().int().min(0),
    agencyId: z.string().optional(),
  });

// Types
export type SmsList = typeof smsLists.$inferSelect;
export type InsertSmsList = z.infer<typeof insertSmsListSchema>;

// SMS Send Events (for analytics tracking)
export const smsSendEvents = pgTable(
  "sms_send_events",
  {
    id: serial("id").primaryKey(),
    smsId: integer("sms_id").notNull(), // references sms_messages.id
    eventType: varchar("event_type", { length: 50 }).notNull(), // 'sent', 'delivered', 'failed', 'undelivered'
    eventData: jsonb("event_data"), // additional event metadata
    occurredAt: timestamp("occurred_at").defaultNow(),
  },
  (table) => ({
    smsIdIdx: index("sms_send_events_sms_id_idx").on(table.smsId),
    eventTypeIdx: index("sms_send_events_event_type_idx").on(table.eventType),
    smsIdEventTypeIdx: index("sms_send_events_sms_id_event_type_idx").on(
      table.smsId,
      table.eventType
    ),
  })
);

export const insertSmsSendEventSchema = createInsertSchema(smsSendEvents)
  .omit({ id: true, occurredAt: true })
  .extend({
    eventType: z.enum(["sent", "delivered", "failed", "undelivered", "stop_reply"]),
    eventData: z.record(z.any()).optional(),
  });

export type SmsSendEvent = typeof smsSendEvents.$inferSelect;
export type InsertSmsSendEvent = z.infer<typeof insertSmsSendEventSchema>;

// Form Field Types for Web-to-Lead Forms
export const formFieldTypeSchema = z.enum([
  "text",
  "email",
  "phone",
  "textarea",
  "number",
  "select",
  "checkbox",
  "radio",
  "date",
]);

export type FormFieldType = z.infer<typeof formFieldTypeSchema>;

export const formFieldSchema = z.object({
  id: z.string(),
  name: z.string(),
  label: z.string(),
  type: formFieldTypeSchema,
  required: z.boolean().default(false),
  placeholder: z.string().optional(),
  options: z.array(z.string()).optional(), // For select, radio, checkbox types
});

export type FormField = z.infer<typeof formFieldSchema>;

// Web-to-Lead Form Folders
export const webToLeadFormFolders = pgTable(
  "web_to_lead_form_folders",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(),
    description: text("description"),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    agencyIdIdx: index("idx_web_to_lead_form_folders_agency_id").on(
      table.agencyId
    ),
  })
);

// Web-to-Lead Forms
export const webToLeadForms = pgTable(
  "web_to_lead_forms",
  {
    id: serial("id").primaryKey(),
    name: text("name").notNull(), // Internal form name
    title: text("title").notNull(), // Form title shown to users
    description: text("description"),
    fields: jsonb("fields").notNull(), // Array of FormField objects
    submitText: text("submit_text").default("Submit"),
    submitAction: varchar("submit_action", { length: 20 }).notNull().default("message"), // 'message' | 'redirect'
    successMessage: text("success_message"),
    redirectUrl: text("redirect_url"),
    isActive: boolean("is_active").default(true),
    folderId: integer("folder_id"),
    agencyId: varchar("agency_id"),
    createdAt: timestamp("created_at").defaultNow(),
    updatedAt: timestamp("updated_at").defaultNow(),
  },
  (table) => ({
    folderIdIdx: index("idx_web_to_lead_forms_folder_id").on(table.folderId),
    agencyIdIdx: index("idx_web_to_lead_forms_agency_id").on(table.agencyId),
    isActiveIdx: index("idx_web_to_lead_forms_is_active").on(table.isActive),
  })
);

// Web-to-Lead Submissions
export const webToLeadSubmissions = pgTable(
  "web_to_lead_submissions",
  {
    id: serial("id").primaryKey(),
    formId: integer("form_id").notNull(), // references web_to_lead_forms.id
    submittedData: jsonb("submitted_data").notNull(), // All form field values (standard + custom)
    prospectId: integer("prospect_id"), // references prospects.id (if prospect was created)
    ipAddress: varchar("ip_address", { length: 45 }), // IPv4 or IPv6
    userAgent: text("user_agent"),
    createdAt: timestamp("created_at").defaultNow(),
  },
  (table) => ({
    formIdIdx: index("web_to_lead_submissions_form_id_idx").on(table.formId),
    prospectIdIdx: index("web_to_lead_submissions_prospect_id_idx").on(table.prospectId),
    createdAtIdx: index("web_to_lead_submissions_created_at_idx").on(table.createdAt),
  })
);

// Insert Schemas
export const insertWebToLeadFormFolderSchema = createInsertSchema(
  webToLeadFormFolders
).omit({ id: true, createdAt: true, updatedAt: true });

export const insertWebToLeadFormSchema = createInsertSchema(webToLeadForms)
  .omit({ id: true, createdAt: true, updatedAt: true })
  .extend({
    fields: z.array(formFieldSchema).min(1, "At least one form field is required"),
    submitAction: z.enum(["message", "redirect"]),
    successMessage: z.string().optional(),
    redirectUrl: z.string().url().optional(),
  });

export const insertWebToLeadSubmissionSchema = createInsertSchema(
  webToLeadSubmissions
).omit({ id: true, createdAt: true })
  .extend({
    submittedData: z.record(z.any()), // Free-form JSON data from submission
  });

// Types
export type WebToLeadFormFolder = typeof webToLeadFormFolders.$inferSelect;
export type InsertWebToLeadFormFolder = z.infer<
  typeof insertWebToLeadFormFolderSchema
>;

export type WebToLeadForm = typeof webToLeadForms.$inferSelect;
export type InsertWebToLeadForm = z.infer<typeof insertWebToLeadFormSchema>;

export type WebToLeadSubmission = typeof webToLeadSubmissions.$inferSelect;
export type InsertWebToLeadSubmission = z.infer<
  typeof insertWebToLeadSubmissionSchema
>;
